import { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context';
import api from '../api/client';
import { WysiwygEditor } from '../components/Shared';
import '../styles/admin.css';

// Badge images (admin panel)
const BADGE_IMGS_ADMIN = {};
for (let i = 1; i <= 28; i++) {
  try { BADGE_IMGS_ADMIN[i] = require(`../assets/badges/Badges_${i}.webp`); } catch {} // eslint-disable-line
}

// ── Shared export/import helpers ─────────────────────────
function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function toXml(items, rootTag, rowTag) {
  const doc = document.implementation.createDocument('', '', null);
  const root = doc.createElement(rootTag);
  items.forEach(item => {
    const el = doc.createElement(rowTag);
    Object.entries(item).forEach(([k, v]) => {
      const f = doc.createElement(k);
      f.textContent = Array.isArray(v) ? v.join(',') : String(v ?? '');
      el.appendChild(f);
    });
    root.appendChild(el);
  });
  doc.appendChild(root);
  return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(doc);
}

function fromXml(text, rowTag) {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  return [...doc.getElementsByTagName(rowTag)].map(el => {
    const obj = {};
    [...el.children].forEach(c => { obj[c.tagName] = c.textContent; });
    return obj;
  });
}

async function fromExcel(file) {
  const ab = await file.arrayBuffer();
  const wb = XLSX.read(ab, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { defval: '' });
}

function toExcel(items, sheetName, filename) {
  const ws = XLSX.utils.json_to_sheet(items.map(r => {
    const flat = { ...r };
    if (Array.isArray(flat.tags)) flat.tags = flat.tags.join(', ');
    return flat;
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename + '.xlsx');
}

const STATUS_COLOR = s => ({ approved:'#50e080', pending:'#f0c040', rejected:'#ff5050', draft:'#7b8fbf' }[s] || '#aaa');

const TABS = [
  { id: 'dashboard', label: 'Statistika',    icon: '📊' },
  { id: 'users',     label: 'Vartotojai',    icon: '👥' },
  { id: 'clubs',     label: 'Klubai',        icon: '🏰' },
  { id: 'events',    label: 'Renginiai',     icon: '📅' },
  { id: 'news',      label: 'Naujienos',     icon: '📰' },
  { id: 'locations', label: 'Vietos',        icon: '📍' },
  { id: 'ads',       label: 'Reklamos',      icon: '📢' },
  { id: 'log',       label: 'Prisijungimai', icon: '📋' },
  { id: 'heatmap',   label: 'Heatmap',       icon: '🔥' },
  { id: 'badges',    label: 'Ženkliukai',    icon: '🏅' },
  { id: 'oauth',     label: 'OAuth',         icon: '🔑' },
];

function RoleBadge({ role }) {
  return <span className={`adm-role adm-role-${role}`}>{role}</span>;
}

function StatusBadge({ status }) {
  return <span className={`adm-status adm-status-${status}`}>{status}</span>;
}

// ── Dashboard tab ────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="adm-loading">Kraunama...</div>;

  const cards = [
    { label: 'Vartotojai',    value: stats.users,          sub: null },
    { label: 'Klubai',        value: stats.clubs,          sub: stats.pending_clubs  ? `${stats.pending_clubs} laukia` : null },
    { label: 'Renginiai',     value: stats.events,         sub: stats.pending_events ? `${stats.pending_events} laukia` : null },
    { label: 'Naujienos',     value: stats.news,           sub: stats.pending_news   ? `${stats.pending_news} laukia`  : null },
  ];

  return (
    <>
      <div className="adm-section-title">ADMINISTRAVIMO SKYDELIS</div>
      <div className="adm-stats-grid">
        {cards.map(c => (
          <div key={c.label} className="adm-stat-card">
            <div className="adm-stat-label">{c.label}</div>
            <div className="adm-stat-value">{c.value}</div>
            {c.sub && <div className="adm-stat-sub">⚠ {c.sub}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

// ── Users tab ────────────────────────────────────────────
function UsersTab({ currentUserId }) {
  const [data,    setData]    = useState(null);
  const [q,       setQ]       = useState('');
  const [role,    setRole]    = useState('');
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/users', { params: { q, role, page, limit: 20 } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q, role, page]);

  useEffect(() => { load(); }, [load]);

  const handleRoleChange = async (userId, newRole) => {
    await api.put(`/admin/users/${userId}`, { role: newRole });
    load();
  };

  const handleToggle = async (userId, currentActive) => {
    await api.put(`/admin/users/${userId}`, { active: currentActive ? 0 : 1 });
    load();
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Ištrinti vartotoją "${name}"? Šis veiksmas negrįžtamas.`)) return;
    await api.delete(`/admin/users/${userId}`);
    load();
  };

  const handleResetPw = async (userId, name) => {
    if (!window.confirm(`Sugeneruoti naują laikiną slaptažodį vartotojui "${name}"?`)) return;
    try {
      const r = await api.post(`/admin/users/${userId}/reset-password`);
      const { temp_password, email } = r.data;
      window.alert(`Laikinas slaptažodis vartotojui ${name} (${email}):\n\n${temp_password}\n\nPerduokite šį slaptažodį vartotojui.`);
    } catch { window.alert('Klaida keičiant slaptažodį'); }
  };

  return (
    <>
      <div className="adm-section-title">VARTOTOJAI</div>
      <div className="adm-toolbar">
        <input
          className="adm-search"
          placeholder="Ieškoti pagal vardą arba el. paštą..."
          value={q}
          onChange={e => { setQ(e.target.value); setPage(1); }}
        />
        <select className="adm-select" value={role} onChange={e => { setRole(e.target.value); setPage(1); }}>
          <option value="">Visi vaidmenys</option>
          <option value="admin">Admin</option>
          <option value="partner">Partner</option>
          <option value="user">User</option>
        </select>
      </div>

      {loading && <div className="adm-loading">Kraunama...</div>}

      {!loading && data && (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Vardas</th>
                  <th>El. paštas</th>
                  <th>Vaidmuo</th>
                  <th>Patvirt.</th>
                  <th>Statusas</th>
                  <th>Registruotas</th>
                  <th>Veiksmai</th>
                </tr>
              </thead>
              <tbody>
                {data.users.length === 0 && (
                  <tr><td colSpan={8} className="adm-empty">Vartotojų nerasta</td></tr>
                )}
                {data.users.map(u => (
                  <tr key={u.id}>
                    <td className="adm-id-cell">#{u.id}</td>
                    <td className="adm-name-cell">
                      {u.name}
                      {u.id === currentUserId && <span style={{ marginLeft: 6, fontSize: 10, color: 'rgba(240,192,64,0.6)' }}>(tu)</span>}
                    </td>
                    <td className="adm-email-cell">{u.email}</td>
                    <td>
                      {u.id !== currentUserId ? (
                        <select
                          className="adm-role-select"
                          value={u.role}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                        >
                          <option value="user">user</option>
                          <option value="partner">partner</option>
                          <option value="admin">admin</option>
                        </select>
                      ) : (
                        <RoleBadge role={u.role} />
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: 12, color: u.email_verified ? '#50e080' : 'rgba(255,255,255,0.25)' }}>
                        {u.email_verified ? '✓' : '✗'}
                      </span>
                    </td>
                    <td>
                      <span className={`adm-active-badge ${u.active ? 'on' : 'off'}`}>
                        {u.active ? '● Aktyvus' : '○ Blokuotas'}
                      </span>
                    </td>
                    <td className="adm-date-cell">{(u.created_at || '').slice(0, 10)}</td>
                    <td>
                      <div className="adm-actions">
                        {u.id !== currentUserId && (
                          <>
                            <button
                              className={`adm-btn adm-btn-sm ${u.active ? 'adm-btn-toggle' : 'adm-btn-approve'}`}
                              onClick={() => handleToggle(u.id, u.active)}
                            >
                              {u.active ? 'Blokuoti' : 'Atblokuoti'}
                            </button>
                            <button
                              className="adm-btn adm-btn-sm"
                              onClick={() => handleResetPw(u.id, u.name)}
                              title="Naujas laikinas slaptažodis"
                            >
                              🔑 Reset
                            </button>
                            <button
                              className="adm-btn adm-btn-sm adm-btn-delete"
                              onClick={() => handleDelete(u.id, u.name)}
                            >
                              Ištrinti
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (() => {
            const total = data.pages;
            const pages = [];
            const add = (n) => { if (!pages.includes(n) && n >= 1 && n <= total) pages.push(n); };
            add(1); add(2);
            add(page - 1); add(page); add(page + 1);
            add(total - 1); add(total);
            pages.sort((a, b) => a - b);
            const withEllipsis = [];
            pages.forEach((p, i) => {
              if (i > 0 && p - pages[i - 1] > 1) withEllipsis.push('…');
              withEllipsis.push(p);
            });
            return (
              <div className="adm-pagination">
                <button className="adm-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
                {withEllipsis.map((p, i) =>
                  p === '…'
                    ? <span key={'e' + i} style={{ padding: '0 4px', color: 'var(--text3)' }}>…</span>
                    : <button key={p} className={`adm-page-btn${p === page ? ' current' : ''}`} onClick={() => setPage(p)}>{p}</button>
                )}
                <button className="adm-page-btn" disabled={page >= total} onClick={() => setPage(p => p + 1)}>→</button>
                <span className="adm-page-info">{data.total} vartotojų</span>
              </div>
            );
          })()}
        </>
      )}
    </>
  );
}

// ── Clubs tab ────────────────────────────────────────────
const CLUB_TYPES = ['club', 'larp', 'rpg', 'ccg', 'boardgames', 'cosplay', 'other'];

function ClubsTab() {
  const [data,       setData]       = useState(null);
  const [status,     setStatus]     = useState('');
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [editClub,   setEditClub]   = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [importMsg,  setImportMsg]  = useState('');
  const importRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/clubs', { params: { status, page, limit: 20 } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const setClubStatus = async (id, newStatus) => {
    await api.patch(`/admin/clubs/${id}/status`, null, { params: { status: newStatus } });
    load();
  };

  const openEdit = async (c) => {
    try {
      const r = await api.get(`/clubs/${c.id}`);
      const full = r.data;
      setEditForm({
        name:        full.name        || '',
        emoji:       full.emoji       || '🐉',
        type:        full.type        || 'club',
        city:        full.city        || '',
        country:     full.country     || '',
        description: full.description || '',
        rules:       full.rules       || '',
        founded:     full.founded     || '',
        status:      c.status         || 'pending',
        owner_id:    full.owner_id    != null ? String(full.owner_id) : '',
        image_url:   full.image_url   || '',
        youtube_url: full.youtube_url || '',
      });
      setEditClub(c);
    } catch {}
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const body = {
        ...editForm,
        owner_id: editForm.owner_id ? parseInt(editForm.owner_id, 10) : null,
      };
      await api.put(`/admin/clubs/${editClub.id}`, body);
      setEditClub(null);
      load();
    } catch {}
    setEditSaving(false);
  };

  const exportClubs = async () => {
    try {
      const r = await api.get('/admin/clubs/export');
      downloadBlob(JSON.stringify(r.data, null, 2), 'clubs.json', 'application/json');
    } catch {}
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const clubs = JSON.parse(text);
      if (!Array.isArray(clubs)) { setImportMsg('Klaida: JSON turi būti masyvas'); return; }
      const r = await api.post('/admin/clubs/import', clubs);
      setImportMsg(`Importuota: +${r.data.created} nauji, ~${r.data.updated} atnaujinti`);
      load();
    } catch (err) {
      setImportMsg('Importo klaida');
    }
    setTimeout(() => setImportMsg(''), 4000);
  };

  // smart pagination (same as UsersTab)
  const renderPagination = () => {
    if (!data || data.pages <= 1) return null;
    const total = data.pages;
    const pages = [];
    const add = (n) => { if (!pages.includes(n) && n >= 1 && n <= total) pages.push(n); };
    add(1); add(2);
    add(page - 1); add(page); add(page + 1);
    add(total - 1); add(total);
    pages.sort((a, b) => a - b);
    const withEllipsis = [];
    pages.forEach((p, i) => {
      if (i > 0 && p - pages[i - 1] > 1) withEllipsis.push('…');
      withEllipsis.push(p);
    });
    return (
      <div className="adm-pagination">
        <button className="adm-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
        {withEllipsis.map((p, i) =>
          p === '…'
            ? <span key={'e' + i} style={{ padding: '0 4px', color: 'var(--text3)' }}>…</span>
            : <button key={p} className={`adm-page-btn${p === page ? ' current' : ''}`} onClick={() => setPage(p)}>{p}</button>
        )}
        <button className="adm-page-btn" disabled={page >= total} onClick={() => setPage(p => p + 1)}>→</button>
        <span className="adm-page-info">{data.total} klubų</span>
      </div>
    );
  };

  return (
    <>
      <div className="adm-section-title">KLUBAI</div>
      <div className="adm-toolbar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="adm-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Visi statusai</option>
          <option value="pending">Laukiantys</option>
          <option value="approved">Patvirtinti</option>
          <option value="rejected">Atmesti</option>
        </select>
        <button className="adm-btn adm-btn-sm" onClick={exportClubs} style={{ marginLeft: 'auto' }}>⬇ Eksportuoti JSON</button>
        <button className="adm-btn adm-btn-sm" onClick={() => importRef.current?.click()}>⬆ Importuoti JSON</button>
        <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
        {importMsg && <span style={{ fontSize: 12, color: 'rgba(140,230,140,0.9)' }}>{importMsg}</span>}
      </div>

      {loading && <div className="adm-loading">Kraunama...</div>}

      {!loading && data && (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Klubas</th>
                  <th>Tipas</th>
                  <th>Miestas</th>
                  <th>Savininkas</th>
                  <th>Statusas</th>
                  <th>Sukurtas</th>
                  <th>Veiksmai</th>
                </tr>
              </thead>
              <tbody>
                {data.clubs.length === 0 && (
                  <tr><td colSpan={8} className="adm-empty">Klubų nerasta</td></tr>
                )}
                {data.clubs.map(c => (
                  <tr key={c.id}>
                    <td className="adm-id-cell">#{c.id}</td>
                    <td className="adm-name-cell">{c.emoji} {c.name}</td>
                    <td><span style={{ fontSize: 11, color: 'rgba(210,195,250,0.55)' }}>{c.type}</span></td>
                    <td><span style={{ fontSize: 12 }}>{c.city}</span></td>
                    <td className="adm-email-cell">{c.owner_name || '—'}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className="adm-date-cell">{(c.created_at || '').slice(0, 10)}</td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn adm-btn-sm adm-btn-toggle" onClick={() => openEdit(c)}>✎ Redaguoti</button>
                        {c.status !== 'approved' && (
                          <button className="adm-btn adm-btn-sm adm-btn-approve" onClick={() => setClubStatus(c.id, 'approved')}>✓</button>
                        )}
                        {c.status !== 'rejected' && (
                          <button className="adm-btn adm-btn-sm adm-btn-reject" onClick={() => setClubStatus(c.id, 'rejected')}>✗</button>
                        )}
                        {c.status !== 'pending' && (
                          <button className="adm-btn adm-btn-sm" onClick={() => setClubStatus(c.id, 'pending')}>↩</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderPagination()}
        </>
      )}

      {/* Edit modal */}
      {editClub && (
        <div className="adm-modal-backdrop" onClick={() => setEditClub(null)}>
          <div className="adm-modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="adm-modal-title">Redaguoti klubą #{editClub.id}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
              <label style={{ gridColumn: '1/3' }}>
                <span className="adm-field-label">Pavadinimas *</span>
                <input className="adm-input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              </label>
              <label>
                <span className="adm-field-label">Emoji</span>
                <input className="adm-input" value={editForm.emoji} onChange={e => setEditForm(f => ({ ...f, emoji: e.target.value }))} />
              </label>
              <label>
                <span className="adm-field-label">Tipas</span>
                <select className="adm-select" style={{ width: '100%' }} value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}>
                  {CLUB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>
                <span className="adm-field-label">Miestas</span>
                <input className="adm-input" value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
              </label>
              <label>
                <span className="adm-field-label">Šalis</span>
                <input className="adm-input" value={editForm.country} onChange={e => setEditForm(f => ({ ...f, country: e.target.value }))} placeholder="LT" />
              </label>
              <label>
                <span className="adm-field-label">Įkurtas (metai)</span>
                <input className="adm-input" value={editForm.founded} onChange={e => setEditForm(f => ({ ...f, founded: e.target.value }))} />
              </label>
              <label>
                <span className="adm-field-label">Statusas</span>
                <select className="adm-select" style={{ width: '100%' }} value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </select>
              </label>
              <label>
                <span className="adm-field-label">Savininko ID</span>
                <input className="adm-input" value={editForm.owner_id} onChange={e => setEditForm(f => ({ ...f, owner_id: e.target.value }))} placeholder="vartotojo ID" />
              </label>
              <label style={{ gridColumn: '1/3' }}>
                <span className="adm-field-label">Paveikslėlio URL</span>
                <input className="adm-input" value={editForm.image_url} onChange={e => setEditForm(f => ({ ...f, image_url: e.target.value }))} />
              </label>
              <label style={{ gridColumn: '1/3' }}>
                <span className="adm-field-label">Aprašymas</span>
                <textarea className="adm-textarea" rows={4} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="adm-btn" onClick={() => setEditClub(null)}>Atšaukti</button>
              <button className="adm-btn adm-btn-approve" onClick={saveEdit} disabled={editSaving || !editForm.name}>
                {editSaving ? 'Saugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Login log tab ────────────────────────────────────────
// ── Heatmap canvas renderer ─────────────────────────────
function renderHeatmap(canvas, points, total) {
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);
  if (!points.length) return;

  const maxCnt = Math.max(...points.map(p => p.cnt));
  const R = Math.min(W, H) * 0.055;

  // Step 1: draw intensity circles on alpha channel
  points.forEach(p => {
    const cx = p.x * W, cy = p.y * H;
    const intensity = 0.12 + (p.cnt / maxCnt) * 0.88;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    grad.addColorStop(0,   `rgba(0,0,0,${(intensity * 0.9).toFixed(3)})`);
    grad.addColorStop(0.4, `rgba(0,0,0,${(intensity * 0.5).toFixed(3)})`);
    grad.addColorStop(1,   'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.fill();
  });

  // Step 2: colormap (alpha → color)
  const imgData = ctx.getImageData(0, 0, W, H);
  const d = imgData.data;
  // stops: [r, g, b, a] at t=0..1
  const stops = [
    [0,   0,   255, 0  ],  // 0.00 transparent blue
    [0,   0,   255, 80 ],  // 0.15 blue
    [0,   200, 255, 130],  // 0.30 cyan
    [0,   255, 100, 170],  // 0.50 green
    [255, 255, 0,   200],  // 0.70 yellow
    [255, 130, 0,   220],  // 0.85 orange
    [255, 0,   0,   255],  // 1.00 red
  ];
  const lerp = (a, b, t) => a + (b - a) * t;
  const colorAt = (t) => {
    const seg = (stops.length - 1) * t;
    const i = Math.min(stops.length - 2, Math.floor(seg));
    const f = seg - i;
    return stops[i].map((v, k) => Math.round(lerp(v, stops[i + 1][k], f)));
  };

  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3] / 255;
    if (a === 0) continue;
    const [r, g, b, na] = colorAt(a);
    d[i] = r; d[i+1] = g; d[i+2] = b; d[i+3] = na;
  }
  ctx.putImageData(imgData, 0, 0);
}

function HeatmapTab() {
  const [paths,     setPaths]    = useState([]);
  const [selPath,   setSelPath]  = useState('');
  const [days,      setDays]     = useState(30);
  const [points,    setPoints]   = useState(null);
  const [total,     setTotal]    = useState(0);
  const [loading,   setLoading]  = useState(false);
  const [error,     setError]    = useState('');
  const canvasRef = useRef();

  // Load path list
  useEffect(() => {
    api.get('/admin/heatmap/paths', { params: { days } })
      .then(r => { setPaths(r.data); if (r.data.length && !selPath) setSelPath(r.data[0].path); })
      .catch(() => {});
  }, [days]); // eslint-disable-line

  // Load click data for selected path
  useEffect(() => {
    if (!selPath && paths.length === 0) return;
    setLoading(true); setError('');
    api.get('/admin/heatmap/data', { params: { path: selPath, days } })
      .then(r => { setPoints(r.data.points); setTotal(r.data.total); })
      .catch(e => setError(e?.response?.data?.detail || 'Klaida kraunant'))
      .finally(() => setLoading(false));
  }, [selPath, days]); // eslint-disable-line

  // Draw whenever points change
  useEffect(() => {
    if (!canvasRef.current || !points) return;
    renderHeatmap(canvasRef.current, points, total);
  }, [points, total]);

  const handleClear = async () => {
    if (!window.confirm(`Ištrinti ${selPath ? selPath : 'visus'} paspaudimų duomenis?`)) return;
    try {
      await api.delete('/admin/heatmap/clear', { params: { path: selPath } });
      setPoints([]); setTotal(0);
      api.get('/admin/heatmap/paths', { params: { days } }).then(r => setPaths(r.data)).catch(() => {});
    } catch { setError('Klaida trinant'); }
  };

  const topPath = paths[0];

  return (
    <>
      <div className="adm-section-title">WEBSITE HEATMAP</div>
      {error && <div className="adm-error-bar">⚠ {error} <button onClick={()=>setError('')}>×</button></div>}

      <div className="adm-toolbar" style={{flexWrap:'wrap', gap:8}}>
        <select className="adm-select" value={selPath} onChange={e=>setSelPath(e.target.value)} style={{flex:1, minWidth:180}}>
          <option value="">Visos puslapiai</option>
          {paths.map(p => (
            <option key={p.path} value={p.path}>{p.path} ({p.cnt})</option>
          ))}
        </select>
        <select className="adm-select" value={days} onChange={e=>setDays(Number(e.target.value))}>
          <option value={1}>Šiandien</option>
          <option value={7}>7 dienos</option>
          <option value={30}>30 dienų</option>
          <option value={90}>90 dienų</option>
          <option value={9999}>Visi laikai</option>
        </select>
        <button className="adm-btn adm-btn-sm adm-btn-reject" onClick={handleClear}>
          🗑 Išvalyti
        </button>
      </div>

      {/* Stats row */}
      <div className="adm-hm-stats">
        <div className="adm-hm-stat">
          <div className="adm-hm-stat-val">{total.toLocaleString()}</div>
          <div className="adm-hm-stat-lbl">Paspaudimai</div>
        </div>
        <div className="adm-hm-stat">
          <div className="adm-hm-stat-val">{paths.length}</div>
          <div className="adm-hm-stat-lbl">Puslapiai</div>
        </div>
        <div className="adm-hm-stat">
          <div className="adm-hm-stat-val">{points?.length ?? 0}</div>
          <div className="adm-hm-stat-lbl">Unikalios vietos</div>
        </div>
        {topPath && (
          <div className="adm-hm-stat" style={{flex:2}}>
            <div className="adm-hm-stat-val" style={{fontSize:12, fontFamily:'monospace'}}>{topPath.path}</div>
            <div className="adm-hm-stat-lbl">Populiariausias puslapis ({topPath.cnt})</div>
          </div>
        )}
      </div>

      {/* Heatmap canvas */}
      <div className="adm-hm-wrap">
        {/* Colour legend */}
        <div className="adm-hm-legend">
          <span>Mažai</span>
          <div className="adm-hm-gradient" />
          <span>Daug</span>
        </div>

        <div className="adm-hm-canvas-wrap">
          {loading && <div className="adm-hm-overlay">Kraunama...</div>}
          {!loading && points?.length === 0 && (
            <div className="adm-hm-overlay">
              <div style={{fontSize:40, marginBottom:12}}>🖱️</div>
              <div>Nėra paspaudimų duomenų</div>
              <div style={{fontSize:11, opacity:.5, marginTop:6}}>Paspaudimai renkami automatiškai</div>
            </div>
          )}
          <canvas
            ref={canvasRef}
            width={1280}
            height={720}
            className="adm-hm-canvas"
          />
          {/* Grid overlay for reference */}
          <div className="adm-hm-grid" />
        </div>

        {/* Top clicked areas */}
        {points?.length > 0 && (
          <div className="adm-hm-top">
            <div className="adm-hm-top-title">TOP paspaudimai</div>
            {[...points].sort((a,b) => b.cnt - a.cnt).slice(0,8).map((p,i) => (
              <div key={i} className="adm-hm-top-row">
                <span className="adm-hm-top-rank">#{i+1}</span>
                <span className="adm-hm-top-pos">
                  X:{Math.round(p.x*100)}% Y:{Math.round(p.y*100)}%
                </span>
                <div className="adm-hm-top-bar-wrap">
                  <div className="adm-hm-top-bar"
                    style={{width: `${Math.round((p.cnt/points[0]?.cnt||1)*100)}%`}} />
                </div>
                <span className="adm-hm-top-cnt">{p.cnt}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function LogTab() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get('/admin/login-log', { params: { limit: 100 } })
      .then(r => setRows(r.data))
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <div className="adm-loading">Kraunama...</div>;

  return (
    <>
      <div className="adm-section-title">PRISIJUNGIMŲ ISTORIJA</div>
      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Vartotojas</th>
              <th>El. paštas</th>
              <th>Statusas</th>
              <th>Priežastis</th>
              <th>IP</th>
              <th>Data</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="adm-empty">Įrašų nėra</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="adm-name-cell">{r.name || '—'}</td>
                <td className="adm-email-cell">{r.email}</td>
                <td>
                  <span className={r.success ? 'adm-log-ok' : 'adm-log-bad'}>
                    {r.success ? '✓ Sėkmingas' : '✗ Nesėkmingas'}
                  </span>
                </td>
                <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{r.reason || '—'}</td>
                <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{r.ip_address}</td>
                <td className="adm-date-cell">{(r.created_at || '').replace('T', ' ').slice(0, 16)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Events moderation tab ────────────────────────────────
const EV_TYPES = ['event','larp','fest','tournament','workshop','rpg','ccg'];

function EventsTab() {
  const [data,      setData]      = useState(null);
  const [status,    setStatus]    = useState('');
  const [q,         setQ]         = useState('');
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [editing,   setEditing]   = useState(null);
  const [importing,        setImporting]        = useState(null);
  const [larpImporting,    setLarpImporting]    = useState(false);
  const [larpResults,      setLarpResults]      = useState(null);
  const [larpCountry,      setLarpCountry]      = useState('italy');
  const [sitemapImporting, setSitemapImporting] = useState(false);
  const [sitemapResults,   setSitemapResults]   = useState(null);
  const importRef = useRef();

  const load = useCallback(() => {
    setLoading(true); setError('');
    api.get('/admin/events', { params: { status, q, page, limit: 20 } })
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Klaida kraunant'))
      .finally(() => setLoading(false));
  }, [status, q, page]);

  useEffect(() => { load(); }, [load]);

  const setEvStatus = async (id, s) => {
    try { await api.patch(`/admin/events/${id}/status`, null, { params: { status: s } }); load(); }
    catch(e) { setError(e?.response?.data?.detail || 'Klaida keičiant statusą'); }
  };

  const handleSaveEdit = async (form) => {
    try {
      await api.put(`/admin/events/${editing.id}`, {
        name: form.name, date: form.date, city: form.city, country: form.country,
        location: form.location, type: form.type, description: form.description,
        rules: form.rules || '', youtube_url: form.youtube_url || '',
        max_participants: form.max_participants || 100, club_id: form.club_id || null,
        image_url: form.image_url || '',
      });
      setEditing(null); load();
    } catch(e) { setError(e?.response?.data?.detail || 'Klaida išsaugant'); }
  };

  const handleExport = async (fmt) => {
    try {
      const r = await api.get('/admin/events/export');
      const rows = r.data;
      if (fmt === 'json') downloadBlob(JSON.stringify(rows, null, 2), 'events', 'application/json');
      else if (fmt === 'xml') downloadBlob(toXml(rows, 'events', 'event'), 'events.xml', 'application/xml');
      else toExcel(rows, 'Renginiai', 'events');
    } catch { setError('Eksportavimo klaida'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = '';
    try {
      let items;
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        items = await fromExcel(file);
      } else if (file.name.endsWith('.xml')) {
        items = fromXml(await file.text(), 'event');
      } else {
        items = JSON.parse(await file.text());
      }
      setImporting({ done: 0, total: items.length });
      const r = await api.post('/admin/events/import', items);
      setImporting({ done: r.data.inserted, total: items.length });
      setTimeout(() => { setImporting(null); load(); }, 2000);
    } catch(e) { setError('Importavimo klaida: ' + (e?.response?.data?.detail || e?.message || '')); setImporting(null); }
  };

  const handleLarpImport = async (country) => {
    setLarpImporting(true); setLarpResults(null); setError('');
    try {
      const r = await api.post('/admin/events/import-larp', null, { params: { country }, timeout: 180000 });
      setLarpResults(r.data.results);
      if (r.data.imported > 0) load();
    } catch(e) {
      setError('Klaida: ' + (e?.response?.data?.detail || e?.message || 'Nepavyko importuoti'));
    } finally { setLarpImporting(false); }
  };

  const handleSitemapImport = async () => {
    setSitemapImporting(true); setSitemapResults(null); setError('');
    try {
      const r = await api.post('/admin/events/import-larp-sitemap', null, { params: { limit: 100 }, timeout: 300000 });
      setSitemapResults(r.data);
      if (r.data.imported > 0) load();
    } catch(e) {
      setError('Klaida: ' + (e?.response?.data?.detail || e?.message || 'Nepavyko importuoti'));
    } finally { setSitemapImporting(false); }
  };

  return (
    <>
      <div className="adm-section-title">RENGINIAI</div>

      {error && <div className="adm-error-bar">⚠ {error} <button onClick={()=>setError('')}>×</button></div>}

      <div className="adm-toolbar">
        <input className="adm-search" style={{flex:1}} placeholder="Ieškoti..." value={q}
          onChange={e=>{setQ(e.target.value);setPage(1);}} />
        <select className="adm-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
          <option value="">Visi</option>
          <option value="pending">Laukiantys</option>
          <option value="approved">Patvirtinti</option>
          <option value="rejected">Atmesti</option>
        </select>
        <div className="adm-export-group">
          <span className="adm-export-label">Eksportas:</span>
          <button className="adm-btn adm-btn-sm" onClick={()=>handleExport('json')}>JSON</button>
          <button className="adm-btn adm-btn-sm" onClick={()=>handleExport('xml')}>XML</button>
          <button className="adm-btn adm-btn-sm" onClick={()=>handleExport('excel')}>Excel</button>
        </div>
        <div className="adm-export-group">
          <span className="adm-export-label">Importas:</span>
          <button className="adm-btn adm-btn-sm adm-btn-approve" onClick={()=>importRef.current.click()}>
            ⬆ JSON/XML/Excel
          </button>
          <input ref={importRef} type="file" accept=".json,.xml,.xlsx,.xls"
            style={{display:'none'}} onChange={handleImport} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={larpCountry}
            onChange={e => setLarpCountry(e.target.value)}
            disabled={larpImporting}
            style={{ background: '#1e1830', border: '1px solid #3a2860', color: '#ede8f8', borderRadius: 6, padding: '4px 8px', fontSize: 12, height: 30 }}
          >
            {[
              ['germany','Vokietija'],['italy','Italija'],['france','Prancūzija'],
              ['poland','Lenkija'],['spain','Ispanija'],['netherlands','Nyderlandai'],
              ['belgium','Belgija'],['austria','Austrija'],['switzerland','Šveicarija'],
              ['czechia','Čekija'],['slovakia','Slovakija'],['hungary','Vengrija'],
              ['finland','Suomija'],['sweden','Švedija'],['norway','Norvegija'],
              ['denmark','Danija'],['united-kingdom','JK'],['ireland','Airija'],
              ['portugal','Portugalija'],['greece','Graikija'],['romania','Rumunija'],
              ['bulgaria','Bulgarija'],['croatia','Kroatija'],['slovenia','Slovėnija'],
              ['lithuania','Lietuva'],['latvia','Latvija'],['estonia','Estija'],
              ['ukraine','Ukraina'],['russia','Rusija'],['usa','JAV'],
              ['canada','Kanada'],['australia','Australija'],
            ].map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
          <input
            value={larpCountry}
            onChange={e => setLarpCountry(e.target.value.toLowerCase().replace(/\s+/g,'-'))}
            disabled={larpImporting}
            placeholder="arba įveskite šalį..."
            style={{ background: '#1e1830', border: '1px solid #3a2860', color: '#ede8f8', borderRadius: 6, padding: '4px 8px', fontSize: 12, height: 30, width: 140 }}
          />
          <button
            className="adm-btn adm-btn-sm adm-btn-ext-import"
            disabled={larpImporting || !larpCountry.trim()}
            onClick={() => handleLarpImport(larpCountry.trim())}
          >
            {larpImporting ? '⏳ Importuojama...' : '🗺️ larp-radar.com'}
          </button>
        </div>
        <button
          className="adm-btn adm-btn-sm adm-btn-ext-import"
          disabled={sitemapImporting}
          onClick={handleSitemapImport}
        >
          {sitemapImporting ? '⏳ Importuojama...' : '🌐 Sitemap (visi)'}
        </button>
      </div>

      {larpResults && (
        <div className="adm-ext-results">
          <div className="adm-ext-results-header">
            Importuota: {larpResults.filter(r=>r.status==='ok').length} |
            Praleista: {larpResults.filter(r=>r.status==='skip').length} |
            Klaida: {larpResults.filter(r=>r.status==='error').length}
            <button style={{marginLeft:'auto',background:'none',border:'none',color:'#aaa',cursor:'pointer'}}
              onClick={()=>setLarpResults(null)}>×</button>
          </div>
          {larpResults.map((r,i) => (
            <div key={i} className={`adm-ext-result-row adm-ext-${r.status}`}>
              <span className="adm-ext-status">
                {r.status==='ok' ? '✓' : r.status==='skip' ? '⊘' : '✗'}
              </span>
              <span className="adm-ext-title">{r.name || r.url}</span>
              {r.date && <span style={{fontSize:10,opacity:.6,marginLeft:6}}>{r.date}</span>}
              {r.reason && <span className="adm-ext-reason">— {r.reason}</span>}
            </div>
          ))}
        </div>
      )}

      {sitemapResults && (
        <div className="adm-ext-results">
          <div className="adm-ext-results-header">
            🌐 Sitemap — Rasta: {sitemapResults.total_found} |
            Importuota: {sitemapResults.results.filter(r=>r.status==='ok').length} |
            Praleista: {sitemapResults.results.filter(r=>r.status==='skip').length} |
            Klaida: {sitemapResults.results.filter(r=>r.status==='error').length}
            <button style={{marginLeft:'auto',background:'none',border:'none',color:'#aaa',cursor:'pointer'}}
              onClick={()=>setSitemapResults(null)}>×</button>
          </div>
          {sitemapResults.results.map((r,i) => (
            <div key={i} className={`adm-ext-result-row adm-ext-${r.status}`}>
              <span className="adm-ext-status">
                {r.status==='ok' ? '✓' : r.status==='skip' ? '⊘' : '✗'}
              </span>
              <span className="adm-ext-title">{r.name || r.url}</span>
              {r.date && <span style={{fontSize:10,opacity:.6,marginLeft:6}}>{r.date}</span>}
              {r.reason && <span className="adm-ext-reason">— {r.reason}</span>}
            </div>
          ))}
        </div>
      )}

      {importing && (
        <div className="adm-progress-wrap">
          <div className="adm-progress-label">
            {importing.done < importing.total
              ? `Importuojama... (${importing.done}/${importing.total})`
              : `✓ Importuota ${importing.done} renginių`}
          </div>
          <div className="adm-progress-bar">
            <div className="adm-progress-fill"
              style={{width: `${importing.total ? (importing.done/importing.total)*100 : 0}%`}} />
          </div>
        </div>
      )}

      {loading && <div className="adm-loading">Kraunama...</div>}
      {!loading && data && (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th>ID</th><th>Pavadinimas</th><th>Tipas</th><th>Data</th>
                <th>Miestas</th><th>Šalis</th><th>Statusas</th><th>Veiksmai</th>
              </tr></thead>
              <tbody>
                {(!data.events || data.events.length === 0) &&
                  <tr><td colSpan={8} className="adm-empty">Renginių nerasta</td></tr>}
                {(data.events || []).map(ev => (
                  <tr key={ev.id}>
                    <td className="adm-id-cell">#{ev.id}</td>
                    <td className="adm-name-cell">{ev.name}</td>
                    <td><span className="adm-type-badge">{ev.type}</span></td>
                    <td className="adm-date-cell">{(ev.date||'').slice(0,10)}</td>
                    <td>{ev.city}</td>
                    <td>{ev.country||'—'}</td>
                    <td><span className="adm-status-dot" style={{color:STATUS_COLOR(ev.status)}}>● {ev.status}</span></td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn adm-btn-sm adm-btn-toggle" title="Redaguoti" onClick={()=>setEditing(ev)}>✏</button>
                        {ev.status !== 'approved' && <button className="adm-btn adm-btn-sm adm-btn-approve" title="Patvirtinti" onClick={()=>setEvStatus(ev.id,'approved')}>✓</button>}
                        {ev.status !== 'rejected' && <button className="adm-btn adm-btn-sm adm-btn-reject"  title="Atmesti"    onClick={()=>setEvStatus(ev.id,'rejected')}>✗</button>}
                        {ev.status !== 'pending'  && <button className="adm-btn adm-btn-sm"                 title="Laukti"     onClick={()=>setEvStatus(ev.id,'pending')}>↩</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.pages > 1 && (
            <div className="adm-pagination">
              <button className="adm-page-btn" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Ankst.</button>
              {Array.from({length:data.pages},(_,i)=>i+1).map(p=>(
                <button key={p} className={`adm-page-btn${p===page?' current':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              <button className="adm-page-btn" disabled={page>=data.pages} onClick={()=>setPage(p=>p+1)}>Kitas →</button>
              <span className="adm-page-info">({data.total} iš viso)</span>
            </div>
          )}
        </>
      )}
      {editing && (
        <EventEditModal ev={editing} onSave={handleSaveEdit} onClose={()=>setEditing(null)} />
      )}
    </>
  );
}

function EventEditModal({ ev, onSave, onClose }) {
  const [form, setForm] = useState({ ...ev });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef();
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/upload', fd, { headers:{'Content-Type':'multipart/form-data'} });
      set('image_url', r.data.url);
    } catch { setErr('Nepavyko įkelti nuotraukos'); }
    e.target.value = '';
  };

  const doSave = async () => {
    setSaving(true); setErr('');
    try { await onSave(form); }
    catch(e) { setErr(e?.response?.data?.detail || 'Klaida'); }
    setSaving(false);
  };

  return (
    <div className="adm-modal-backdrop" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="adm-modal-box" style={{maxWidth:580}}>
        <div className="adm-modal-title">✏️ Redaguoti renginį</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div style={{gridColumn:'1/-1'}}><label className="adm-label">Pavadinimas</label><input className="adm-input" value={form.name||''} onChange={e=>set('name',e.target.value)} /></div>
          <div><label className="adm-label">Data</label><input className="adm-input" type="date" value={(form.date||'').slice(0,10)} onChange={e=>set('date',e.target.value)} /></div>
          <div><label className="adm-label">Tipas</label>
            <select className="adm-select" value={form.type||'event'} onChange={e=>set('type',e.target.value)}>
              {EV_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><label className="adm-label">Miestas</label><input className="adm-input" value={form.city||''} onChange={e=>set('city',e.target.value)} /></div>
          <div><label className="adm-label">Šalis</label><input className="adm-input" value={form.country||''} onChange={e=>set('country',e.target.value)} placeholder="LT" /></div>
          <div style={{gridColumn:'1/-1'}}><label className="adm-label">Vieta</label><input className="adm-input" value={form.location||''} onChange={e=>set('location',e.target.value)} /></div>
          <div style={{gridColumn:'1/-1'}}><label className="adm-label">Aprašymas</label><WysiwygEditor value={form.description||''} onChange={v=>set('description',v)} placeholder="Renginio aprašymas..." minHeight={120} /></div>
          <div><label className="adm-label">Maks. dalyviai</label><input className="adm-input" type="number" value={form.max_participants||100} onChange={e=>set('max_participants',parseInt(e.target.value)||100)} /></div>
          <div>
            <label className="adm-label">Paveikslėlis</label>
            <div style={{display:'flex',gap:6}}>
              <input className="adm-input" style={{flex:1}} value={form.image_url||''} onChange={e=>set('image_url',e.target.value)} placeholder="/uploads/..." />
              <button className="adm-btn adm-btn-sm" onClick={()=>fileRef.current.click()}>⬆</button>
            </div>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" style={{display:'none'}} onChange={handleUpload} />
            {form.image_url && <img src={form.image_url} alt="" style={{marginTop:6,maxHeight:60,maxWidth:'100%',borderRadius:4,objectFit:'cover'}} />}
          </div>
        </div>
        {err && <div className="adm-error-bar" style={{marginBottom:10}}>⚠ {err}</div>}
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="adm-btn" onClick={onClose}>Atšaukti</button>
          <button className="adm-btn adm-btn-approve" onClick={doSave} disabled={saving}>{saving?'Saugoma...':'Išsaugoti'}</button>
        </div>
      </div>
    </div>
  );
}

// ── News moderation tab ───────────────────────────────────
const NEWS_CATS = ['Naujienos','RPG','LARP','CCG','Bendruomenė'];

function NewsTab() {
  const [data,         setData]         = useState(null);
  const [status,       setStatus]       = useState('');
  const [q,            setQ]            = useState('');
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [editing,      setEditing]      = useState(null);
  const [importing,    setImporting]    = useState(null);
  const [extImporting, setExtImporting] = useState(false);
  const [extResults,   setExtResults]   = useState(null);
  const [rssImporting, setRssImporting] = useState(false);
  const [rssResults,   setRssResults]   = useState(null);
  const importRef = useRef();

  const load = useCallback(() => {
    setLoading(true); setError('');
    api.get('/admin/news', { params: { status, q, page, limit: 20 } })
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || e?.message || 'Klaida kraunant'))
      .finally(() => setLoading(false));
  }, [status, q, page]);

  useEffect(() => { load(); }, [load]);

  const setNewsStatus = async (id, s) => {
    try { await api.patch(`/admin/news/${id}/status`, null, { params: { status: s } }); load(); }
    catch(e) { setError(e?.response?.data?.detail || 'Klaida keičiant statusą'); }
  };

  const handleSaveEdit = async (form) => {
    try {
      await api.put(`/admin/news/${editing.id}`, {
        title: form.title, content: form.content, category: form.category,
        subcategory: form.subcategory || '', country: form.country || '',
        tags: Array.isArray(form.tags) ? form.tags : form.tags.split(',').map(t=>t.trim()).filter(Boolean),
        image_url: form.image_url || '',
      });
      setEditing(null); load();
    } catch(e) { setError(e?.response?.data?.detail || 'Klaida išsaugant'); }
  };

  const handleExport = async (fmt) => {
    try {
      const r = await api.get('/admin/news/export');
      const rows = r.data;
      if (fmt === 'json') downloadBlob(JSON.stringify(rows, null, 2), 'news.json', 'application/json');
      else if (fmt === 'xml') downloadBlob(toXml(rows, 'news', 'article'), 'news.xml', 'application/xml');
      else toExcel(rows, 'Naujienos', 'news');
    } catch { setError('Eksportavimo klaida'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = '';
    try {
      let items;
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        items = await fromExcel(file);
      } else if (file.name.endsWith('.xml')) {
        items = fromXml(await file.text(), 'article');
      } else {
        items = JSON.parse(await file.text());
      }
      if (!Array.isArray(items)) throw new Error('Neteisingas formatas');
      setImporting({ done: 0, total: items.length });
      const r = await api.post('/admin/news/import', items);
      setImporting({ done: r.data.inserted, total: items.length });
      setTimeout(() => { setImporting(null); load(); }, 2000);
    } catch(e) { setError('Importavimo klaida: ' + (e?.response?.data?.detail || e?.message || '')); setImporting(null); }
  };

  const handleImportExternal = async () => {
    setExtImporting(true); setExtResults(null); setError('');
    try {
      const r = await api.post('/admin/news/import-external', null, { timeout: 180000 });
      setExtResults(r.data.results);
      if (r.data.imported > 0) load();
    } catch(e) {
      setError('Klaida: ' + (e?.response?.data?.detail || e?.message || 'Nepavyko importuoti'));
    } finally { setExtImporting(false); }
  };

  const handleImportRss = async () => {
    setRssImporting(true); setRssResults(null); setError('');
    try {
      const r = await api.post('/admin/news/import-rss', null, { timeout: 180000 });
      setRssResults(r.data.results);
      if (r.data.imported > 0) load();
    } catch(e) {
      setError('Klaida: ' + (e?.response?.data?.detail || e?.message || 'Nepavyko importuoti'));
    } finally { setRssImporting(false); }
  };

  return (
    <>
      <div className="adm-section-title">NAUJIENOS</div>

      {error && <div className="adm-error-bar">⚠ {error} <button onClick={()=>setError('')}>×</button></div>}

      <div className="adm-toolbar">
        <input className="adm-search" style={{flex:1}} placeholder="Ieškoti pagal pavadinimą ar autorių..." value={q}
          onChange={e=>{setQ(e.target.value);setPage(1);}} />
        <select className="adm-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
          <option value="">Visos</option>
          <option value="draft">Juodraščiai</option>
          <option value="pending">Laukiančios</option>
          <option value="approved">Patvirtintos</option>
          <option value="rejected">Atmestos</option>
        </select>
        <div className="adm-export-group">
          <span className="adm-export-label">Eksportas:</span>
          <button className="adm-btn adm-btn-sm" onClick={()=>handleExport('json')}>JSON</button>
          <button className="adm-btn adm-btn-sm" onClick={()=>handleExport('xml')}>XML</button>
          <button className="adm-btn adm-btn-sm" onClick={()=>handleExport('excel')}>Excel</button>
        </div>
        <div className="adm-export-group">
          <span className="adm-export-label">Importas:</span>
          <button className="adm-btn adm-btn-sm adm-btn-approve" onClick={()=>importRef.current.click()}>
            ⬆ JSON/XML/Excel
          </button>
          <input ref={importRef} type="file" accept=".json,.xml,.xlsx,.xls"
            style={{display:'none'}} onChange={handleImport} />
        </div>
        <button
          className="adm-btn adm-btn-sm adm-btn-ext-import"
          onClick={handleImportExternal}
          disabled={extImporting}
          title="Importuoti paskutines 5 naujienas iš ttrpgfans.com, išversti į LT ir išsaugoti kaip juodraštį"
        >
          {extImporting ? '⏳ Importuojama...' : '🌐 ttrpgfans.com'}
        </button>
        <button
          className="adm-btn adm-btn-sm adm-btn-ext-import"
          onClick={handleImportRss}
          disabled={rssImporting}
          title="Importuoti iš RSS: Bell of Lost Souls, Tribality, The Kind GM (automatiškai kas dieną 7:00)"
        >
          {rssImporting ? '⏳ Importuojama...' : '📡 RSS Feeds'}
        </button>
      </div>

      {importing && (
        <div className="adm-progress-wrap">
          <div className="adm-progress-label">
            {importing.done < importing.total
              ? `Importuojama... (${importing.done}/${importing.total})`
              : `✓ Importuota ${importing.done} naujienų`}
          </div>
          <div className="adm-progress-bar">
            <div className="adm-progress-fill"
              style={{width: `${importing.total ? (importing.done/importing.total)*100 : 0}%`}} />
          </div>
        </div>
      )}

      {extResults && (
        <div className="adm-ext-results">
          <div className="adm-ext-results-header">
            <span>🌐 Importo rezultatai — {extResults.filter(r=>r.status==='ok').length} importuota</span>
            <button onClick={()=>setExtResults(null)}>×</button>
          </div>
          {extResults.map((r, i) => (
            <div key={i} className={`adm-ext-result-row adm-ext-${r.status}`}>
              <span className="adm-ext-status">
                {r.status === 'ok' ? '✓' : r.status === 'skip' ? '↷' : '✗'}
              </span>
              <span className="adm-ext-title">{r.title || r.url}</span>
              {r.reason && <span className="adm-ext-reason">{r.reason}</span>}
              {r.status === 'ok' && (
                <button className="adm-btn adm-btn-sm adm-btn-approve"
                  onClick={()=>{ setNewsStatus(r.id,'approved'); setExtResults(prev=>prev.map((x,j)=>j===i?{...x,published:true}:x)); }}>
                  Skelbti
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {rssResults && (
        <div className="adm-ext-results">
          <div className="adm-ext-results-header">
            <span>📡 RSS — {rssResults.filter(r=>r.status==='ok').length} importuota | {rssResults.filter(r=>r.status==='skip').length} praleista</span>
            <button onClick={()=>setRssResults(null)}>×</button>
          </div>
          {rssResults.map((r, i) => (
            <div key={i} className={`adm-ext-result-row adm-ext-${r.status}`}>
              <span className="adm-ext-status">
                {r.status === 'ok' ? '✓' : r.status === 'skip' ? '↷' : '✗'}
              </span>
              {r.source && <span style={{fontSize:10,opacity:.5,marginRight:4}}>[{r.source}]</span>}
              <span className="adm-ext-title">{r.title || r.url}</span>
              {r.reason && <span className="adm-ext-reason">{r.reason}</span>}
              {r.status === 'ok' && (
                <button className="adm-btn adm-btn-sm adm-btn-approve"
                  onClick={()=>{ setNewsStatus(r.id,'approved'); setRssResults(prev=>prev.map((x,j)=>j===i?{...x,published:true}:x)); }}>
                  Skelbti
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {loading && <div className="adm-loading">Kraunama...</div>}
      {!loading && data && (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th>ID</th><th>Pavadinimas</th><th>Kategorija</th>
                <th>Šalis</th><th>Autorius</th><th>Statusas</th><th>Sukurta</th><th>Veiksmai</th>
              </tr></thead>
              <tbody>
                {(!data.news || data.news.length === 0) &&
                  <tr><td colSpan={8} className="adm-empty">Naujienų nerasta</td></tr>}
                {(data.news || []).map(n => (
                  <tr key={n.id}>
                    <td className="adm-id-cell">#{n.id}</td>
                    <td className="adm-name-cell">
                      {n.source_url && <span className="adm-ext-badge" title={n.source_url}>🌐</span>}
                      {n.title}
                    </td>
                    <td><span className="adm-type-badge">{n.category}</span></td>
                    <td>{n.country||'—'}</td>
                    <td className="adm-email-cell">{n.author_name||'—'}</td>
                    <td><span className="adm-status-dot" style={{color:STATUS_COLOR(n.status)}}>● {n.status}</span></td>
                    <td className="adm-date-cell">{(n.created_at||'').slice(0,10)}</td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn adm-btn-sm adm-btn-toggle" title="Redaguoti" onClick={()=>setEditing(n)}>✏</button>
                        {n.status !== 'approved' && <button className="adm-btn adm-btn-sm adm-btn-approve" title="Skelbti" onClick={()=>setNewsStatus(n.id,'approved')}>✓</button>}
                        {n.status !== 'rejected' && <button className="adm-btn adm-btn-sm adm-btn-reject"  title="Atmesti"    onClick={()=>setNewsStatus(n.id,'rejected')}>✗</button>}
                        {n.status === 'draft' && <button className="adm-btn adm-btn-sm" title="Laukti" onClick={()=>setNewsStatus(n.id,'pending')}>↩</button>}
                        {n.status !== 'pending' && n.status !== 'draft' && <button className="adm-btn adm-btn-sm" title="Laukti" onClick={()=>setNewsStatus(n.id,'pending')}>↩</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.pages > 1 && (
            <div className="adm-pagination">
              <button className="adm-page-btn" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Ankst.</button>
              {Array.from({length:data.pages},(_,i)=>i+1).map(p=>(
                <button key={p} className={`adm-page-btn${p===page?' current':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              <button className="adm-page-btn" disabled={page>=data.pages} onClick={()=>setPage(p=>p+1)}>Kitas →</button>
              <span className="adm-page-info">({data.total} iš viso)</span>
            </div>
          )}
        </>
      )}
      {editing && (
        <NewsEditModal news={editing} onSave={handleSaveEdit} onClose={()=>setEditing(null)} />
      )}
    </>
  );
}

function ImageGalleryPicker({ onSelect, onClose }) {
  const [files,   setFiles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [q,       setQ]       = useState('');
  const [imgErr,  setImgErr]  = useState({});

  useEffect(() => {
    setLoading(true);
    api.get('/admin/uploads', { params: { q, limit: 60 } })
      .then(r => setFiles(r.data.files))
      .catch(() => setFiles([]))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <div className="adm-modal-backdrop" onClick={e=>e.target===e.currentTarget&&onClose()} style={{zIndex:3000}}>
      <div className="adm-modal-box" style={{maxWidth:720,maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
          <div className="adm-modal-title" style={{margin:0}}>Galerija</div>
          <input
            className="adm-search"
            style={{flex:1,margin:0}}
            placeholder="Ieškoti failo..."
            value={q}
            onChange={e=>setQ(e.target.value)}
          />
          <button className="adm-btn adm-btn-sm adm-btn-reject" onClick={onClose}>×</button>
        </div>
        <div style={{overflowY:'auto',flex:1}}>
          {loading && <div className="adm-loading">Kraunama...</div>}
          {!loading && files.length === 0 && (
            <div className="adm-empty" style={{padding:32,textAlign:'center'}}>Paveikslėlių nerasta</div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',gap:8,padding:4}}>
            {files.map(f => (
              <button
                key={f.url}
                onClick={() => onSelect(f.url)}
                style={{
                  background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',
                  borderRadius:8,padding:6,cursor:'pointer',display:'flex',flexDirection:'column',
                  alignItems:'center',gap:4,transition:'border-color .15s',
                }}
                onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(240,192,64,0.6)'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'}
              >
                {imgErr[f.url] ? (
                  <div style={{width:'100%',height:80,display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,opacity:.4}}>🖼️</div>
                ) : (
                  <img
                    src={f.url} alt=""
                    onError={()=>setImgErr(p=>({...p,[f.url]:true}))}
                    style={{width:'100%',height:80,objectFit:'cover',borderRadius:4}}
                  />
                )}
                <span style={{fontSize:9,color:'rgba(255,255,255,0.4)',wordBreak:'break-all',textAlign:'center',lineHeight:1.2}}>
                  {f.name.slice(0,24)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewsEditModal({ news, onSave, onClose }) {
  const [form, setForm] = useState({
    ...news,
    tags: Array.isArray(news.tags) ? news.tags.join(', ') : (news.tags || ''),
  });
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState('');
  const [imgBroken,   setImgBroken]   = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const fileRef = useRef();
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/upload', fd, { headers:{'Content-Type':'multipart/form-data'} });
      set('image_url', r.data.url);
      setImgBroken(false);
    } catch { setErr('Nepavyko įkelti nuotraukos'); }
    e.target.value = '';
  };

  const handleUrlChange = (v) => { set('image_url', v); setImgBroken(false); };
  const handleGallerySelect = (url) => { set('image_url', url); setImgBroken(false); setShowGallery(false); };

  const doSave = async () => {
    if (!form.title?.trim()) { setErr('Pavadinimas privalomas'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ ...form, tags: form.tags.split(',').map(t=>t.trim()).filter(Boolean) });
    } catch(e) { setErr(e?.response?.data?.detail || 'Klaida'); }
    setSaving(false);
  };

  return (
    <>
      <div className="adm-modal-backdrop" onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div className="adm-modal-box" style={{maxWidth:640}}>
          <div className="adm-modal-title">✏️ Redaguoti naujieną</div>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div><label className="adm-label">Pavadinimas *</label><input className="adm-input" value={form.title||''} onChange={e=>set('title',e.target.value)} /></div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
              <div><label className="adm-label">Kategorija</label>
                <select className="adm-select" value={form.category||'Naujienos'} onChange={e=>set('category',e.target.value)}>
                  {NEWS_CATS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="adm-label">Šalis</label><input className="adm-input" value={form.country||''} onChange={e=>set('country',e.target.value)} placeholder="LT" /></div>
              <div><label className="adm-label">Statusas</label>
                <select className="adm-select" value={form.status||'pending'} onChange={e=>set('status',e.target.value)}>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
            </div>
            <div><label className="adm-label">Žymos (per kablelį)</label><input className="adm-input" value={form.tags||''} onChange={e=>set('tags',e.target.value)} placeholder="RPG, LARP, D&D" /></div>
            <div><label className="adm-label">Turinys *</label><WysiwygEditor value={form.content||''} onChange={v=>set('content',v)} placeholder="Naujienos turinys..." minHeight={260} /></div>

            {/* ── Image section ── */}
            <div>
              <label className="adm-label">Paveikslėlis</label>

              {/* Preview */}
              {form.image_url && (
                <div style={{position:'relative',marginBottom:8,display:'inline-block',maxWidth:'100%'}}>
                  {imgBroken ? (
                    <div style={{
                      width:240,height:140,borderRadius:8,background:'rgba(255,255,255,0.05)',
                      border:'1px dashed rgba(255,80,80,0.5)',display:'flex',flexDirection:'column',
                      alignItems:'center',justifyContent:'center',gap:6,color:'rgba(255,120,120,0.7)',fontSize:12,
                    }}>
                      <span style={{fontSize:28}}>🖼️</span>
                      <span>Paveikslėlis nepasiekiamas</span>
                    </div>
                  ) : (
                    <img
                      src={form.image_url}
                      alt=""
                      onError={()=>setImgBroken(true)}
                      style={{maxHeight:160,maxWidth:'100%',borderRadius:8,objectFit:'cover',display:'block'}}
                    />
                  )}
                  <button
                    onClick={()=>{set('image_url','');setImgBroken(false);}}
                    title="Ištrinti paveikslėlį"
                    style={{
                      position:'absolute',top:6,right:6,width:24,height:24,borderRadius:'50%',
                      background:'rgba(0,0,0,0.7)',border:'1px solid rgba(255,80,80,0.6)',
                      color:'#ff5050',fontSize:14,lineHeight:1,cursor:'pointer',
                      display:'flex',alignItems:'center',justifyContent:'center',
                    }}
                  >×</button>
                </div>
              )}

              {/* URL input + buttons */}
              <div style={{display:'flex',gap:6}}>
                <input
                  className="adm-input"
                  style={{flex:1}}
                  value={form.image_url||''}
                  onChange={e=>handleUrlChange(e.target.value)}
                  placeholder="/uploads/..."
                />
                <button className="adm-btn adm-btn-sm" onClick={()=>fileRef.current.click()} title="Įkelti naują">⬆ Įkelti</button>
                <button className="adm-btn adm-btn-sm" onClick={()=>setShowGallery(true)} title="Pasirinkti iš galerijos">🖼 Galerija</button>
              </div>
              <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" style={{display:'none'}} onChange={handleUpload} />
            </div>

            {err && <div className="adm-error-bar">⚠ {err}</div>}
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
            <button className="adm-btn" onClick={onClose}>Atšaukti</button>
            <button className="adm-btn adm-btn-approve" onClick={doSave} disabled={saving}>{saving?'Saugoma...':'Išsaugoti'}</button>
          </div>
        </div>
      </div>

      {showGallery && (
        <ImageGalleryPicker onSelect={handleGallerySelect} onClose={()=>setShowGallery(false)} />
      )}
    </>
  );
}

// ── Locations tab ────────────────────────────────────────
const LOC_TYPES   = ['venue','shop','outdoor','other'];
const LOC_STATUS  = ['active','blocked','suspended'];

function LocModal({ initial, onSave, onClose }) {
  const empty = { name:'',type:'venue',city:'',address:'',country:'LT',lat:'',lng:'',rating:'5',hours:'',description:'',status:'active' };
  const [form, setForm] = useState(initial ? { ...initial, lat: String(initial.lat||''), lng: String(initial.lng||''), rating: String(initial.rating||'5') } : empty);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ position:'fixed',inset:0,zIndex:2000,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#0d0a18',border:'1px solid rgba(240,192,64,0.25)',borderRadius:16,maxWidth:560,width:'100%',maxHeight:'90vh',overflow:'auto',padding:28 }}>
        <div style={{ fontFamily:'Cinzel,serif',fontSize:15,color:'#f0c040',marginBottom:20 }}>{initial ? '✏️ Redaguoti vietą' : '➕ Nauja vieta'}</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
          <div style={{ gridColumn:'1/-1' }}>
            <label className="adm-label">Pavadinimas *</label>
            <input className="adm-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Vietos pavadinimas" />
          </div>
          <div>
            <label className="adm-label">Tipas</label>
            <select className="adm-select" value={form.type} onChange={e=>set('type',e.target.value)}>
              {LOC_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="adm-label">Statusas</label>
            <select className="adm-select" value={form.status} onChange={e=>set('status',e.target.value)}>
              {LOC_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="adm-label">Miestas</label>
            <input className="adm-input" value={form.city} onChange={e=>set('city',e.target.value)} placeholder="Vilnius" />
          </div>
          <div>
            <label className="adm-label">Šalis (kodas)</label>
            <input className="adm-input" value={form.country} onChange={e=>set('country',e.target.value)} placeholder="LT" maxLength={3} style={{ textTransform:'uppercase' }} />
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label className="adm-label">Adresas</label>
            <input className="adm-input" value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Gedimino pr. 1, Vilnius" />
          </div>
          <div>
            <label className="adm-label">Platuma (lat)</label>
            <input className="adm-input" type="number" step="any" value={form.lat} onChange={e=>set('lat',e.target.value)} placeholder="54.6872" />
          </div>
          <div>
            <label className="adm-label">Ilguma (lng)</label>
            <input className="adm-input" type="number" step="any" value={form.lng} onChange={e=>set('lng',e.target.value)} placeholder="25.2797" />
          </div>
          <div>
            <label className="adm-label">Reitingas (1-5)</label>
            <input className="adm-input" type="number" step="0.1" min="1" max="5" value={form.rating} onChange={e=>set('rating',e.target.value)} />
          </div>
          <div>
            <label className="adm-label">Darbo valandos</label>
            <input className="adm-input" value={form.hours} onChange={e=>set('hours',e.target.value)} placeholder="I-V 10-21" />
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label className="adm-label">Aprašymas</label>
            <WysiwygEditor value={form.description||''} onChange={v=>set('description',v)} placeholder="Vietos aprašymas..." minHeight={120} />
          </div>
        </div>
        <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button className="adm-btn" onClick={onClose}>Atšaukti</button>
          <button className="adm-btn adm-btn-approve" onClick={() => onSave({ ...form, lat: parseFloat(form.lat)||0, lng: parseFloat(form.lng)||0, rating: parseFloat(form.rating)||5 })}>
            Išsaugoti
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationsTab() {
  const [data,    setData]   = useState(null);
  const [status,  setStatus] = useState('');
  const [q,       setQ]      = useState('');
  const [page,    setPage]   = useState(1);
  const [loading, setLoading]= useState(false);
  const [editing,  setEditing]  = useState(null); // null | false | locationObj
  const [importing,setImporting]= useState(null); // null | { done, total }
  const importRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/locations', { params: { status, q, page, limit: 30 } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, q, page]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (editing && editing.id) {
      await api.put(`/admin/locations/${editing.id}`, form);
    } else {
      await api.post('/admin/locations', form);
    }
    setEditing(null);
    load();
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Ištrinti "${loc.name}"?`)) return;
    await api.delete(`/admin/locations/${loc.id}`);
    load();
  };

  const handleStatus = async (loc, newStatus) => {
    await api.patch(`/admin/locations/${loc.id}/status`, null, { params: { status: newStatus } });
    load();
  };

  const handleExport = async () => {
    const r = await api.get('/admin/locations/export');
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'locations.json'; a.click();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    try {
      const arr = JSON.parse(text);
      setImporting({ done: 0, total: arr.length });
      const r = await api.post('/admin/locations/import', arr);
      setImporting({ done: r.data.inserted, total: arr.length });
      setTimeout(() => { setImporting(null); load(); }, 2000);
    } catch { alert('Neteisingas JSON formatas'); setImporting(null); }
    e.target.value = '';
  };

  const statusColor = s => ({ active:'#50e080', blocked:'#e05050', suspended:'#f0c040' }[s] || '#aaa');

  return (
    <>
      <div className="adm-section-title">VIETOS</div>
      <div className="adm-toolbar" style={{ flexWrap:'wrap', gap:8 }}>
        <input className="adm-search" style={{ flex:1,minWidth:180 }} placeholder="Ieškoti..." value={q} onChange={e=>{setQ(e.target.value);setPage(1);}} />
        <select className="adm-select" value={status} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
          <option value="">Visi statusai</option>
          {LOC_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button className="adm-btn adm-btn-approve" onClick={()=>setEditing(false)}>➕ Nauja</button>
        <button className="adm-btn" onClick={handleExport}>⬇ Eksportuoti</button>
        <button className="adm-btn" onClick={()=>importRef.current.click()}>⬆ Importuoti</button>
        <input ref={importRef} type="file" accept=".json" style={{ display:'none' }} onChange={handleImport} />
      </div>

      {importing && (
        <div style={{ margin:'12px 0',padding:'14px 20px',background:'rgba(240,192,64,0.06)',border:'1px solid rgba(240,192,64,0.2)',borderRadius:10 }}>
          <div style={{ fontSize:12,color:'rgba(200,180,255,0.7)',marginBottom:8 }}>
            {importing.done < importing.total ? `Importuojama... (${importing.done}/${importing.total})` : `✓ Importuota ${importing.done} vietų`}
          </div>
          <div style={{ height:6,background:'rgba(255,255,255,0.06)',borderRadius:3 }}>
            <div style={{ height:'100%',width:`${importing.total ? (importing.done/importing.total)*100 : 0}%`,background:'#f0c040',borderRadius:3,transition:'width .3s' }} />
          </div>
        </div>
      )}

      {loading && <div className="adm-loading">Kraunama...</div>}

      {!loading && data && (
        <>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead><tr>
                <th>ID</th><th>Pavadinimas</th><th>Tipas</th><th>Miestas</th><th>Šalis</th><th>Statusas</th><th>Veiksmai</th>
              </tr></thead>
              <tbody>
                {data.locations.length === 0 && <tr><td colSpan={7} className="adm-empty">Vietų nerasta</td></tr>}
                {data.locations.map(loc => (
                  <tr key={loc.id}>
                    <td className="adm-id-cell">#{loc.id}</td>
                    <td className="adm-name-cell">{loc.name}</td>
                    <td><span style={{ fontSize:11,color:'rgba(210,195,250,0.55)' }}>{loc.type}</span></td>
                    <td>{loc.city}</td>
                    <td>{loc.country}</td>
                    <td><span style={{ fontSize:11,fontWeight:600,color:statusColor(loc.status) }}>● {loc.status}</span></td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn adm-btn-sm adm-btn-toggle" onClick={()=>setEditing(loc)}>✏ Redaguoti</button>
                        {loc.status !== 'active'    && <button className="adm-btn adm-btn-sm adm-btn-approve" onClick={()=>handleStatus(loc,'active')}>✓ Aktyvuoti</button>}
                        {loc.status === 'active'    && <button className="adm-btn adm-btn-sm adm-btn-reject"  onClick={()=>handleStatus(loc,'blocked')}>⊘ Blokuoti</button>}
                        {loc.status !== 'suspended' && <button className="adm-btn adm-btn-sm"                 onClick={()=>handleStatus(loc,'suspended')}>⏸ Sustabdyti</button>}
                        <button className="adm-btn adm-btn-sm adm-btn-delete" onClick={()=>handleDelete(loc)}>✕ Ištrinti</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.pages > 1 && (
            <div className="adm-pagination">
              <button className="adm-page-btn" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Ankst.</button>
              {Array.from({length:data.pages},(_,i)=>i+1).map(p=>(
                <button key={p} className={`adm-page-btn ${p===page?'current':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              <button className="adm-page-btn" disabled={page>=data.pages} onClick={()=>setPage(p=>p+1)}>Kitas →</button>
              <span className="adm-page-info">({data.total} iš viso)</span>
            </div>
          )}
        </>
      )}

      {editing !== null && (
        <LocModal initial={editing || null} onSave={handleSave} onClose={()=>setEditing(null)} />
      )}
    </>
  );
}

// ── Ads tab ──────────────────────────────────────────────
function AdModal({ initial, onSave, onClose }) {
  const empty = { name:'',type:'local',position:'general',active:1,image_url:'',link_url:'',google_code:'' };
  const [form, setForm] = useState(initial ? { ...initial } : empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/admin/ads/upload', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      set('image_url', r.data.url);
    } catch { alert('Nepavyko įkelti failo'); }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div style={{ position:'fixed',inset:0,zIndex:2000,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#0d0a18',border:'1px solid rgba(240,192,64,0.25)',borderRadius:16,maxWidth:520,width:'100%',maxHeight:'90vh',overflow:'auto',padding:28 }}>
        <div style={{ fontFamily:'Cinzel,serif',fontSize:15,color:'#f0c040',marginBottom:20 }}>{initial ? '✏️ Redaguoti reklamą' : '➕ Nauja reklama'}</div>
        <div style={{ display:'flex',flexDirection:'column',gap:12,marginBottom:12 }}>
          <div>
            <label className="adm-label">Pavadinimas *</label>
            <input className="adm-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Vidinė reklamos etiketė" />
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div>
              <label className="adm-label">Tipas</label>
              <select className="adm-select" value={form.type} onChange={e=>set('type',e.target.value)}>
                <option value="local">Vietinė reklama</option>
                <option value="google">Google Ads</option>
              </select>
            </div>
            <div>
              <label className="adm-label">Pozicija</label>
              <select className="adm-select" value={form.position} onChange={e=>set('position',e.target.value)}>
                <option value="general">Bendra</option>
                <option value="calendar">Kalendorius</option>
                <option value="sidebar">Šoninė juosta</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:10 }}>
            <label className="adm-label" style={{ margin:0 }}>Aktyvus</label>
            <input type="checkbox" checked={!!form.active} onChange={e=>set('active',e.target.checked?1:0)} style={{ width:16,height:16,cursor:'pointer' }} />
          </div>

          {form.type === 'local' && (
            <>
              <div>
                <label className="adm-label">Nuorodų URL</label>
                <input className="adm-input" value={form.link_url} onChange={e=>set('link_url',e.target.value)} placeholder="https://example.com" />
              </div>
              <div>
                <label className="adm-label">Paveikslėlis (GIF/PNG/SVG/JPG)</label>
                <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                  <input className="adm-input" style={{ flex:1 }} value={form.image_url} onChange={e=>set('image_url',e.target.value)} placeholder="/uploads/ad.png arba https://..." />
                  <button className="adm-btn adm-btn-sm" onClick={()=>fileRef.current.click()} disabled={uploading}>
                    {uploading ? '...' : '⬆'}
                  </button>
                </div>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" style={{ display:'none' }} onChange={handleUpload} />
                {form.image_url && (
                  <img src={form.image_url} alt="preview" style={{ marginTop:8,maxHeight:80,maxWidth:'100%',borderRadius:6,objectFit:'contain',background:'rgba(255,255,255,0.04)',padding:4 }} />
                )}
              </div>
            </>
          )}

          {form.type === 'google' && (
            <div>
              <label className="adm-label">Google Ads kodas</label>
              <textarea
                className="adm-input"
                rows={6}
                value={form.google_code}
                onChange={e=>set('google_code',e.target.value)}
                placeholder={'<script async src="https://pagead2.googlesyndication.com/..."></script>\n<ins class="adsbygoogle" ...>'}
                style={{ resize:'vertical',fontFamily:'monospace',fontSize:11 }}
              />
              <div style={{ fontSize:11,color:'rgba(200,180,255,0.4)',marginTop:4 }}>
                Įklijuokite Google AdSense arba kito tiekėjo kodą
              </div>
            </div>
          )}
        </div>
        <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button className="adm-btn" onClick={onClose}>Atšaukti</button>
          <button className="adm-btn adm-btn-approve" onClick={()=>onSave(form)}>Išsaugoti</button>
        </div>
      </div>
    </div>
  );
}

function AdsTab() {
  const [ads,     setAds]    = useState([]);
  const [loading, setLoading]= useState(true);
  const [editing, setEditing]= useState(null); // null | false | adObj

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/ads').then(r=>setAds(r.data)).catch(()=>setAds([])).finally(()=>setLoading(false));
  }, []);

  useEffect(()=>{ load(); },[load]);

  const handleSave = async (form) => {
    if (editing && editing.id) { await api.put(`/admin/ads/${editing.id}`, form); }
    else { await api.post('/admin/ads', form); }
    setEditing(null); load();
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Ištrinti reklamą "${ad.name}"?`)) return;
    await api.delete(`/admin/ads/${ad.id}`); load();
  };

  const toggleActive = async (ad) => {
    await api.put(`/admin/ads/${ad.id}`, { ...ad, active: ad.active ? 0 : 1 }); load();
  };

  const typeLabel = t => ({ local:'Vietinė', google:'Google Ads' }[t] || t);
  const posLabel  = p => ({ general:'Bendra', calendar:'Kalendorius', sidebar:'Šoninė' }[p] || p);

  return (
    <>
      <div className="adm-section-title">REKLAMOS</div>
      <div className="adm-toolbar">
        <button className="adm-btn adm-btn-approve" onClick={()=>setEditing(false)}>➕ Nauja reklama</button>
      </div>

      {loading && <div className="adm-loading">Kraunama...</div>}

      {!loading && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead><tr>
              <th>ID</th><th>Pavadinimas</th><th>Tipas</th><th>Pozicija</th><th>Aktyvus</th><th>Paspaudimai</th><th>Veiksmai</th>
            </tr></thead>
            <tbody>
              {ads.length === 0 && <tr><td colSpan={7} className="adm-empty">Reklamos nerasta</td></tr>}
              {ads.map(ad => (
                <tr key={ad.id}>
                  <td className="adm-id-cell">#{ad.id}</td>
                  <td className="adm-name-cell">{ad.name}</td>
                  <td><span style={{ fontSize:11,color:'rgba(210,195,250,0.55)' }}>{typeLabel(ad.type)}</span></td>
                  <td><span style={{ fontSize:11 }}>{posLabel(ad.position)}</span></td>
                  <td>
                    <button
                      className={`adm-btn adm-btn-sm ${ad.active ? 'adm-btn-approve' : ''}`}
                      onClick={()=>toggleActive(ad)}
                      style={{ minWidth:80 }}
                    >
                      {ad.active ? '● Įjungta' : '○ Išjungta'}
                    </button>
                  </td>
                  <td style={{ textAlign:'center',fontFamily:'monospace',color:'#f0c040',fontWeight:700 }}>{ad.clicks ?? 0}</td>
                  <td>
                    <div className="adm-actions">
                      <button className="adm-btn adm-btn-sm adm-btn-toggle" onClick={()=>setEditing(ad)}>✏ Redaguoti</button>
                      <button className="adm-btn adm-btn-sm adm-btn-delete" onClick={()=>handleDelete(ad)}>✕ Ištrinti</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <AdModal initial={editing || null} onSave={handleSave} onClose={()=>setEditing(null)} />
      )}
    </>
  );
}

// ── Badges Tab ───────────────────────────────────────────
function BadgesTab() {
  const [defs, setDefs] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [nomFilter, setNomFilter] = useState('pending');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    api.get('/badges').then(r => setDefs(r.data || [])).catch(() => {});
    api.get('/admin/badge-nominations', { params: { status: nomFilter } })
      .then(r => setNominations(r.data || [])).catch(() => {});
  }, [nomFilter]);

  useEffect(() => { reload(); }, [reload]);

  const saveDef = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/badges/${editing.id}`, { name: editing.name, description: editing.description });
      setEditing(null);
      reload();
    } catch {}
    setSaving(false);
  };

  const approve = async (id) => {
    await api.post(`/admin/badge-nominations/${id}/approve`).catch(() => {});
    reload();
  };

  const reject = async (id) => {
    await api.post(`/admin/badge-nominations/${id}/reject`, {}).catch(() => {});
    reload();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 24, alignItems: 'start' }}>
      {/* Left: badge definitions */}
      <div>
        <div className="adm-section-title" style={{ marginBottom: 14 }}>🏅 Ženkliukų aprašymai</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '60vh', overflowY: 'auto' }}>
          {defs.map(b => (
            <div key={b.id} onClick={() => setEditing({ ...b })}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: editing?.id === b.id ? 'rgba(240,192,64,0.08)' : 'var(--bg)', border: `1px solid ${editing?.id === b.id ? 'var(--gold2)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer' }}>
              <img src={BADGE_IMGS_ADMIN[b.badge_num]} alt="" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.name || <span style={{ color: 'var(--text3)' }}>#{b.badge_num} (be pavadinimo)</span>}
                </div>
                {b.description && <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.description}</div>}
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <div style={{ marginTop: 16, background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 8, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <img src={BADGE_IMGS_ADMIN[editing.badge_num]} alt="" style={{ width: 44, height: 44, objectFit: 'contain' }} />
              <div style={{ fontSize: 13, color: 'var(--gold2)', fontWeight: 600 }}>Ženkliukas #{editing.badge_num}</div>
            </div>
            <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Pavadinimas</label>
            <input className="form-input" style={{ marginBottom: 8, width: '100%', boxSizing: 'border-box' }} value={editing.name}
              onChange={e => setEditing(v => ({ ...v, name: e.target.value }))} placeholder="Pvz.: Veteranas" />
            <label style={{ fontSize: 11, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Aprašymas</label>
            <textarea className="form-input" rows={3} value={editing.description}
              onChange={e => setEditing(v => ({ ...v, description: e.target.value }))}
              placeholder="Už ką skiriamas šis ženkliukas..."
              style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="admin-action" style={{ background: '#4a3870' }} onClick={saveDef} disabled={saving}>{saving ? '...' : '💾 Išsaugoti'}</button>
              <button className="admin-action" style={{ background: '#2a2040' }} onClick={() => setEditing(null)}>Atšaukti</button>
            </div>
          </div>
        )}
      </div>

      {/* Right: nominations */}
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="adm-section-title">📋 Nominacijos</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {['pending', 'approved', 'rejected'].map(s => (
              <button key={s} className="admin-action"
                style={{ background: nomFilter === s ? '#4a3870' : '#1e1830', fontSize: 11 }}
                onClick={() => setNomFilter(s)}>
                {s === 'pending' ? '⏳ Laukia' : s === 'approved' ? '✅ Patvirtinta' : '❌ Atmesta'}
              </button>
            ))}
          </div>
        </div>

        {nominations.length === 0 && (
          <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>Nominacijų nėra</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {nominations.map(n => (
            <div key={n.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <img src={BADGE_IMGS_ADMIN[n.badge_num]} alt="" style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {n.badge_name || `#${n.badge_num}`} → <span style={{ color: '#80b0ff' }}>{n.player_name}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Nominavo: {n.nominator_name} · {(n.created_at || '').slice(0, 10)}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: 'var(--gold2)', fontWeight: 700 }}>👍 {n.vote_count} balsai</div>
                  <div style={{ fontSize: 10, color: ({ pending: '#f0c040', approved: '#50e080', rejected: '#ff5050' })[n.status] || '#aaa', marginTop: 2 }}>{n.status}</div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text)', fontStyle: 'italic', background: 'var(--bg2)', padding: '6px 10px', borderRadius: 5, marginBottom: 8 }}>"{n.reason}"</div>
              {n.status === 'pending' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="admin-action" style={{ background: '#2a5040', fontSize: 11 }} onClick={() => approve(n.id)}>✅ Patvirtinti</button>
                  <button className="admin-action" style={{ background: '#501c1c', fontSize: 11 }} onClick={() => reject(n.id)}>❌ Atmesti</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── OAuth konfigūracijos tab ─────────────────────────────

const OAUTH_PROVIDERS_UI = [
  {
    id: 'google', name: 'Google', icon: '🇬', color: '#4285f4', hasSecret: false,
    fields: { enabled: 'oauth_google_enabled', client_id: 'oauth_google_client_id' },
    howto: [
      'Eikite į https://console.cloud.google.com → APIs & Services → Credentials.',
      'Spauskite „Create Credentials" → „OAuth client ID" → tipas „Web application".',
      'Prie „Authorized JavaScript origins" pridėkite svetainės adresą (žr. „Frontend URL" žemiau).',
      'Nukopijuokite Client ID (baigiasi .apps.googleusercontent.com) į laukelį žemiau. Client Secret nereikalingas – naudojamas Google Identity Services mygtukas.',
    ],
  },
  {
    id: 'facebook', name: 'Facebook', icon: '📘', color: '#1877f2', hasSecret: true,
    fields: { enabled: 'oauth_facebook_enabled', client_id: 'oauth_facebook_client_id', client_secret: 'oauth_facebook_client_secret' },
    howto: [
      'Eikite į https://developers.facebook.com → My Apps → „Create App" (tipas: Consumer).',
      'Pridėkite produktą „Facebook Login" → Settings.',
      'Prie „Valid OAuth Redirect URIs" įklijuokite Redirect URI (rodomas žemiau).',
      'App Settings → Basic: nukopijuokite „App ID" (Client ID) ir „App Secret" (Client Secret).',
      'Kad prisijungti galėtų visi vartotojai, aplikaciją perjunkite į „Live" režimą.',
    ],
  },
  {
    id: 'x', name: 'X (Twitter)', icon: '𝕏', color: '#15181c', hasSecret: true,
    fields: { enabled: 'oauth_x_enabled', client_id: 'oauth_x_client_id', client_secret: 'oauth_x_client_secret' },
    howto: [
      'Eikite į https://developer.x.com → Projects & Apps → sukurkite App.',
      'App settings → „User authentication settings" → Set up: OAuth 2.0, Type of App: „Web App".',
      'Callback URI: įklijuokite Redirect URI (rodomas žemiau). Website URL: svetainės adresas.',
      'Nukopijuokite OAuth 2.0 „Client ID" ir „Client Secret".',
      'Pastaba: X nepateikia el. pašto adreso, todėl paskyra sukuriama su vidiniu identifikatoriumi.',
    ],
  },
  {
    id: 'linkedin', name: 'LinkedIn', icon: '💼', color: '#0a66c2', hasSecret: true,
    fields: { enabled: 'oauth_linkedin_enabled', client_id: 'oauth_linkedin_client_id', client_secret: 'oauth_linkedin_client_secret' },
    howto: [
      'Eikite į https://www.linkedin.com/developers → „Create app".',
      'Products skiltyje pridėkite „Sign In with LinkedIn using OpenID Connect".',
      'Auth skiltyje prie „Authorized redirect URLs" įklijuokite Redirect URI (rodomas žemiau).',
      'Nukopijuokite „Client ID" ir „Client Secret" iš Auth skilties.',
    ],
  },
];

function OAuthTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState('');
  const [showSecret, setShowSecret] = useState({});

  useEffect(() => {
    api.get('/admin/settings/oauth').then(r => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return <div className="adm-loading">Kraunama...</div>;

  const set = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setSaved(false); };

  const save = async () => {
    setSaving(true);
    try {
      const settings = {};
      for (const k of Object.keys(cfg)) {
        if (k.startsWith('oauth_')) settings[k] = cfg[k];
      }
      const r = await api.put('/admin/settings/oauth', { settings });
      setCfg(c => ({ ...c, ...r.data }));
      setSaved(true);
    } finally { setSaving(false); }
  };

  const test = async (pid) => {
    setTesting(pid);
    try {
      await save();
      const r = await api.post(`/admin/settings/oauth/test/${pid}`);
      setTestResults(t => ({ ...t, [pid]: r.data }));
    } catch (e) {
      setTestResults(t => ({ ...t, [pid]: { ok: false, message: e.response?.data?.detail || 'Klaida' } }));
    } finally { setTesting(''); }
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  const inputStyle = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 };

  return (
    <div>
      <div className="adm-section-title">SOCIALINIAI PRISIJUNGIMAI (OAUTH)</div>

      {/* Bendri nustatymai */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 22px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold2)', marginBottom: 12 }}>🌐 Bendri nustatymai</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Redirect bazinis URL (backend)</label>
            <input style={inputStyle} value={cfg.oauth_redirect_base || ''} onChange={e => set('oauth_redirect_base', e.target.value)} placeholder="https://fantastai.lt" />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
              Adresas, kuriuo pasiekiamas FastAPI backend. Testuojant lokaliai: http://localhost:8084
            </div>
          </div>
          <div>
            <label style={labelStyle}>Frontend URL</label>
            <input style={inputStyle} value={cfg.oauth_frontend_url || ''} onChange={e => set('oauth_frontend_url', e.target.value)} placeholder="https://fantastai.lt" />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
              Į šį adresą (+ /oauth-done) vartotojas grąžinamas po prisijungimo. Lokaliai: http://localhost:3000
            </div>
          </div>
        </div>
      </div>

      {/* Tiekėjai */}
      {OAUTH_PROVIDERS_UI.map(p => {
        const enabled = cfg[p.fields.enabled] === '1';
        const redirectUri = cfg.redirect_uris?.[p.id];
        const result = testResults[p.id];
        return (
          <div key={p.id} style={{ background: 'var(--bg2)', border: `1px solid ${enabled ? p.color + '66' : 'var(--border)'}`, borderRadius: 12, padding: '18px 22px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{p.icon}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{p.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: enabled ? 'rgba(80,224,128,0.12)' : 'rgba(136,136,136,0.12)', color: enabled ? '#50e080' : '#888' }}>
                  {enabled ? 'ĮJUNGTA' : 'IŠJUNGTA'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="admin-action" style={{ fontSize: 12 }} onClick={() => set(p.fields.enabled, enabled ? '0' : '1')}>
                  {enabled ? '⏸ Išjungti' : '▶ Įjungti'}
                </button>
                <button className="admin-action" style={{ fontSize: 12, background: '#2a5040' }} disabled={testing === p.id} onClick={() => test(p.id)}>
                  {testing === p.id ? '⏳ Tikrinama...' : '🧪 Testuoti'}
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: p.hasSecret ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 10 }}>
              <div>
                <label style={labelStyle}>Client ID {p.id === 'facebook' ? '(App ID)' : ''}</label>
                <input style={inputStyle} value={cfg[p.fields.client_id] || ''} onChange={e => set(p.fields.client_id, e.target.value)} autoComplete="off" />
              </div>
              {p.hasSecret && (
                <div>
                  <label style={labelStyle}>Client Secret {p.id === 'facebook' ? '(App Secret)' : ''}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      style={{ ...inputStyle, paddingRight: 40 }}
                      type={showSecret[p.id] ? 'text' : 'password'}
                      value={cfg[p.fields.client_secret] || ''}
                      onChange={e => set(p.fields.client_secret, e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(s => ({ ...s, [p.id]: !s[p.id] }))}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text3)' }}
                    >{showSecret[p.id] ? '🙈' : '👁️'}</button>
                  </div>
                </div>
              )}
            </div>

            {redirectUri && (
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Redirect URI (įklijuokite tiekėjo konsolėje)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 8, padding: '7px 12px', fontSize: 12, color: '#f0c040', overflowX: 'auto', whiteSpace: 'nowrap' }}>{redirectUri}</code>
                  <button className="admin-action" style={{ fontSize: 12 }} onClick={() => copy(redirectUri)}>📋 Kopijuoti</button>
                </div>
              </div>
            )}

            {result && (
              <div style={{ fontSize: 13, padding: '8px 14px', borderRadius: 8, marginBottom: 10, background: result.ok ? 'rgba(80,224,128,0.1)' : 'rgba(255,80,80,0.1)', border: `1px solid ${result.ok ? 'rgba(80,224,128,0.35)' : 'rgba(255,80,80,0.35)'}`, color: result.ok ? '#50e080' : '#ff8080' }}>
                {result.ok ? '✅' : '❌'} {result.message}
              </div>
            )}

            <details>
              <summary style={{ fontSize: 12, color: 'var(--text3)', cursor: 'pointer', fontWeight: 600 }}>📖 Kaip gauti raktus</summary>
              <ol style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.8, margin: '8px 0 0', paddingLeft: 20 }}>
                {p.howto.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
            </details>
          </div>
        );
      })}

      {/* Kaip testuoti */}
      <div style={{ background: 'rgba(240,192,64,0.06)', border: '1px solid rgba(240,192,64,0.25)', borderRadius: 12, padding: '16px 22px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold2)', marginBottom: 8 }}>🧪 Kaip testuoti</div>
        <ol style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.9, margin: 0, paddingLeft: 20 }}>
          <li>Įveskite raktus, spauskite „💾 Išsaugoti", tada „🧪 Testuoti" – patikrinami Client ID/Secret tiesiai pas tiekėją.</li>
          <li>Atsijunkite (arba atidarykite inkognito langą) ir prisijungimo lange spauskite naują socialinį mygtuką.</li>
          <li>Po sėkmingo prisijungimo būsite grąžinti į svetainę jau prisijungę; įrašas atsiras skiltyje „Prisijungimai".</li>
          <li>Testuojant lokaliai: Redirect bazinis URL = http://localhost:8084, Frontend URL = http://localhost:3000, ir tuos pačius adresus nurodykite tiekėjo konsolėje.</li>
        </ol>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? '⏳ Saugoma...' : '💾 Išsaugoti'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#50e080' }}>✅ Išsaugota</span>}
      </div>
    </div>
  );
}

// ── Main AdminPage ───────────────────────────────────────
export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate          = useNavigate();
  const [tab, setTab]     = useState('dashboard');
  const [pending, setPending] = useState({});

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    api.get('/admin/stats')
      .then(r => setPending({ clubs: r.data.pending_clubs, events: r.data.pending_events, news: r.data.pending_news }))
      .catch(() => {});
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  const pendingCount = tab => {
    if (tab === 'clubs')  return pending.clubs;
    if (tab === 'events') return pending.events;
    if (tab === 'news')   return pending.news;
    return 0;
  };

  return (
    <div className="adm-wrap">
      {/* Sidebar */}
      <aside className="adm-sidebar">
        <div className="adm-sidebar-title">⚙ ADMIN</div>
        {TABS.map(t => (
          <button
            key={t.id}
            className={`adm-nav-btn ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span className="adm-nav-icon">{t.icon}</span>
            {t.label}
            {pendingCount(t.id) > 0 && <span className="adm-badge">{pendingCount(t.id)}</span>}
          </button>
        ))}
      </aside>

      {/* Content */}
      <main className="adm-main">
        {tab === 'dashboard' && <Dashboard />}
        {tab === 'users'     && <UsersTab currentUserId={user?.id} />}
        {tab === 'clubs'     && <ClubsTab />}
        {tab === 'events'    && <EventsTab />}
        {tab === 'news'      && <NewsTab />}
        {tab === 'locations' && <LocationsTab />}
        {tab === 'ads'       && <AdsTab />}
        {tab === 'log'       && <LogTab />}
        {tab === 'heatmap'   && <HeatmapTab />}
        {tab === 'badges'    && <BadgesTab />}
        {tab === 'oauth'     && <OAuthTab />}
      </main>
    </div>
  );
}
